import type { FilterType, PageComponentName, PreviewMode } from '../../types/pageEditor';
import type { ContainerType, Edge, PageElementType } from './components/dnd-utils';

export const COMPONENT_GROUPS: { [key: string]: string } = {
    CONTAINERS: 'Containers',
    CONTENT: 'Content',
    INPUT: 'Input',
    DATA: 'Data',
    CHARTS: 'Charts',
    CUSTOM: 'Custom',
};

export const ELEMENT_EDGES: Record<Edge, Edge> = {
    top: 'top',
    bottom: 'bottom',
    left: 'left',
    right: 'right',
    all: 'all',
};

export const COMPONENT_PROPERTIES: { label: string; value: string }[] = [
    { label: 'Visibility', value: 'METADATA.VISIBLE' },
    { label: 'Required', value: 'METADATA.REQUIRED' },
    { label: 'Enabled', value: 'METADATA.ENABLED' },
    { label: 'Editable', value: 'METADATA.EDITABLE' },
    { label: 'Value', value: 'VALUE' },
];

export const COMPONENT_CONFIGURATION_PATH: { [key: string]: string[] } = {
    NAME: ['developerName'],
    LABEL: ['label'],
    CONTENT: ['content'],
    IMAGE_URI: ['imageUri'],
    SOURCE: ['attributes', '$src'],
    TARGET: ['attributes', '$target'],
    FILENAME: ['attributes', '$filename'],
    AUTO_UPLOAD: ['attributes', '$autoUpload'],
    SORTABLE_COLUMNS: ['attributes', '$sortableColumns'],
    EXPORT_CSV_BUTTON: ['attributes', '$exportCsvButton'],
    CSV_FILE_NAME: ['attributes', '$csvFileName'],
    AUTOCOMPLETE: ['attributes', '$autocomplete'],
    PAYMENT_CARD_CARD_NUMBER_LABEL: ['attributes', '$paymentCardCardNumberInputLabel'],
    PAYMENT_CARD_EXPIRY_DATE_LABEL: ['attributes', '$paymentCardExpiryDateInputLabel'],
    PAYMENT_CARD_SECURITY_CODE_LABEL: ['attributes', '$paymentCardSecurityCodeInputLabel'],
    PAYMENT_CARD_POSTAL_CODE_LABEL: ['attributes', '$paymentCardPostalCodeInputLabel'],
    PAYMENT_CARD_NAME_ON_CARD_LABEL: ['attributes', '$paymentCardNameOnCardInputLabel'],
    WIDTH: ['width'],
    HEIGHT: ['height'],
    EDITABLE: ['isEditable'],
    MULTISELECT: ['isMultiSelect'],
    REQUIRED: ['isRequired'],
    SEARCHABLE: ['isSearchable'],
    SEARCHABLEDISPLAYCOLUMNS: ['isSearchableDisplayColumns'],
    SEARCHTERMPERSISTED: ['isSearchTermPersisted'],
    STATE_VALUE: ['valueElementValueBindingReferenceId'],
    DATA_SOURCE_LIST: ['valueElementDataBindingReferenceId'],
    DATA_SOURCE_LIST_VALUE: ['valueElementDataBindingReferenceId', 'id'],
    DATA_SOURCE_CONNECTOR: ['objectDataRequest'],
    DATA_SOURCE_CONNECTOR_TYPE: ['objectDataRequest', 'typeElementId'],
    DATA_SOURCE_CONNECTOR_BINDING: ['objectDataRequest', 'typeElementBindingId'],
    DATA_SOURCE_FILTER: ['objectDataRequest', 'listFilter'],
    DATA_SOURCE_2: ['objectDataRequest'],
    DATA_PRESENTATION: ['columns'],
    ROW: ['row'],
    COLUMN: ['column'],
    ROW_SPAN: ['rowSpan'],
    COL_SPAN: ['colSpan'],
    ATTRIBUTES: ['attributes'],
    COMPONENT_TYPE: ['componentType'],
    FILE_DATA_SOURCE: ['fileDataRequest'],
    PAGE_SIZE: ['attributes', '$pageSize'],
    NO_RESULTS_MESSAGE: ['attributes', '$noResultsMessage'],
    SEARCH_TERM: ['attributes', '$searchTerm'],
    FILE_DATA_REQUEST: ['fileDataRequest'],
    FILE_DATA_REQUEST_RESOURCE_PATH: ['fileDataRequest', 'resourcePath'],
    FILE_DATA_REQUEST_CONNECTOR_ID: ['fileDataRequest', 'serviceElementId'],
    FILE_DATA_REQUEST_PATH: ['fileDataRequest', 'path'],
    HELP_INFO: ['helpInfo'],
    HINT_VALUE: ['hintValue'],
    VALIDATIONS: ['validations'],
    CLASSNAME: ['className'],
    LIST_TYPE: ['attributes', '$ordered'],
    ASSISTANT_LABEL: ['attributes', '$assistantLabel'],
    USER_LABEL: ['attributes', '$userLabel'],
    STEP: ['attributes', '$step'],
    IS_SLIDER: ['attributes', '$isSlider'],
    PAGINATION_BUTTONS: ['attributes', '$paginationButtons'],
    ACCOUNT_ID: ['attributes', '$accountId'],
    AGENT_ID: ['attributes', '$agentId'],
    SESSION_ID: ['attributes', '$sessionId'],
    STACKED: ['attributes', '$isStacked'],
    MASK: ['attributes', '$mask'],
    VOICE_INPUT: ['attributes', '$voiceInput'],
    ALERT_ROLE: ['attributes', '$alertRole'],
};

export const COMPONENT_TYPE: { [key: string]: PageComponentName } = {
    INPUT: 'INPUT',
    TEXTAREA: 'TEXTAREA',
    CHECKBOX: 'CHECKBOX',
    CHECKBOXLIST: 'CHECKBOX-LIST',
    TABLE: 'TABLE',
    DATAGRID: 'DATAGRID',
    CALENDAR: 'CALENDAR',
    SELECT: 'SELECT',
    NATIVESELECT: 'NATIVE-SELECT',
    RADIO: 'RADIO',
    TOGGLE: 'TOGGLE',
    CONTENT: 'CONTENT',
    FILEUPLOAD: 'FILE-UPLOAD',
    /** @deprecated */
    FILES: 'FILES',
    TILES: 'TILES',
    CHARTBAR: 'CHART-BAR',
    CHARTLINE: 'CHART-LINE',
    CHARTPIE: 'CHART-PIE',
    CHARTDOUGHNUT: 'CHART-DOUGHNUT',
    CHARTPOLAR: 'CHART-POLAR',
    /** @deprecated */
    HIDDEN: 'HIDDEN',
    PRESENTATION: 'PRESENTATION',
    LIST: 'LIST',
    OUTCOMES: 'OUTCOMES',
    /** @deprecated */
    IMAGE: 'IMAGE',
    PDF: 'PDF-DOWNLOADER',
    PAYMENTCARD: 'PAYMENT-CARD',
    SIGNATUREPAD: 'SIGNATURE-PAD',
    IFRAME: 'IFRAME',
    DOWNLOADBUTTON: 'DOWNLOAD-BUTTON',
    HYPERLINK: 'HYPERLINK',
    CODEGENERATOR: 'CODE-GENERATOR',
    CODESCANNER: 'CODE-SCANNER',
    AICHAT: 'AI-CHAT',
    COLUMN_IMAGE: 'COLUMN-IMAGE',
    COLUMN_HYPERLINK: 'COLUMN-HYPERLINK',
    COLUMN_RELATIVETIME: 'COLUMN-RELATIVE-TIME',
    COLUMN_PROGRESSBAR: 'COLUMN-PROGRESS-BAR',
    NUMBER_INPUT: 'NUMBER-INPUT',
    TEXT_INPUT: 'TEXT-INPUT',
    DATE_INPUT: 'DATE-INPUT',
    DATE_TIME_INPUT: 'DATE-TIME-INPUT',
    MASKED_INPUT: 'MASKED-INPUT',
    PASSWORD_INPUT: 'PASSWORD-INPUT',
    UNKNOWN: 'UNKNOWN',
};

export const COMPONENT_CONFIGURATION_LABELS: { [key: string]: string } = {
    NAME: 'Name',
    LABEL: 'Label',
    CONTENT: 'Content',
    IMAGE_URI: 'Image URI',
    SOURCE: 'URL',
    TARGET: 'Where to open',
    FILENAME: 'Filename',
    AUTO_UPLOAD: 'Begin uploading when a file is selected',
    SORTABLE_COLUMNS: 'Allow for sorting on columns',
    EXPORT_CSV_BUTTON: 'Display export CSV button',
    CSV_FILE_NAME: 'File name of exported CSV',
    AUTOCOMPLETE: 'Autocomplete Tokens',
    WIDTH: 'Width',
    HEIGHT: 'Height',
    EDITABLE: 'Editable',
    MULTISELECT: 'Multi-select',
    SEARCHABLE: 'Searchable',
    SEARCHABLEDISPLAYCOLUMNS: 'Only search display columns',
    SEARCHTERMPERSISTED: 'Persist search term',
    STATE_VALUE: 'Save the component state into',
    DATA_SOURCE_LIST: 'Get the data from a List Value',
    DATA_SOURCE_CONNECTOR: 'Get the data from a Connector',
    DATA_SOURCE_2: 'Data source',
    FILE_DATA_SOURCE: 'Select the connector to load files from',
    DATA_SOURCE_FILTER: 'Data source filter',
    DATA_PRESENTATION: 'Data presentation',
    VALIDATION_MIN_NUMBER: 'Minimum number',
    VALIDATION_MAX_NUMBER: 'Maximum number',
    VALIDATION_MIN_DATE: 'Minimum date',
    VALIDATION_MAX_DATE: 'Maximum date',
    VALIDATION_MIN_DATE_TIME: 'Minimum date and time',
    VALIDATION_MAX_DATE_TIME: 'Maximum date and time',
    VALIDATION_PATTERN: 'Regular expression pattern',
    HINT_VALUE: 'Placeholder text',
    HELP_INFO: 'Help information about this component',
    PAYMENT_CARD_CARD_NUMBER_LABEL: 'Override the "Card number" label',
    PAYMENT_CARD_EXPIRY_DATE_LABEL: 'Override the "Expiry date" label',
    PAYMENT_CARD_SECURITY_CODE_LABEL: 'Override the "Security code" label',
    PAYMENT_CARD_POSTAL_CODE_LABEL: 'Override the "Postal code" label',
    PAYMENT_CARD_NAME_ON_CARD_LABEL: 'Override the "Name on card" label',
    BULLETED_LIST_TYPE: 'Bulleted list',
    NUMBERED_LIST_TYPE: 'Numbered list',
    PAGE_SIZE: 'Page size',
    NO_RESULTS_MESSAGE: 'No results message',
    SEARCH_TERM: 'Search term',
    ASSISTANT_LABEL: 'Assistant label',
    USER_LABEL: 'User label',
    STEP: 'Step size',
    IS_SLIDER: 'Display as slider',
    SCROLL_LOADING: 'Load when scrolling',
    BUTTON_PAGINATION: 'Pagination buttons',
    STACKED: 'Stacked',
    GROUPED_BARS: 'Side by side',
    SESSION_ID: 'Session Id',
    MASK: 'Masking pattern',
    VOICE_INPUT: 'Enable voice input',
    ALERT_ROLE: 'Enable alert role',
};

export const TOGGLE_TRIGGER_OPTIONS: { [key: string]: string } = {
    CHANGE: 'Component changes',
    LOAD: 'Page loads',
};

export const TOGGLE_CATEGORY_OPTIONS: { [key: string]: string } = {
    COMPONENT: 'COMPONENT',
    VALUE: 'VALUE',
};

export const PAGE_OBJECT_REFERENCE_TYPE_OPTIONS: { [key: string]: string } = {
    COMPONENT: 'COMPONENT',
    CONTAINER: 'CONTAINER',
};

// What a page condition does once its rules evaluate to true - either it changes the page through its operations, or it
// moves the flow on through an outcome.
export const PAGE_CONDITION_ACTION_OPTIONS: { [key: string]: string } = {
    OPERATIONS: 'Execute Operations',
    OUTCOME: 'Trigger outcome',
};

export const SUMMARY_CONTEXT: { [key: string]: string } = {
    CONDITIONS: 'CONDITIONS',
    RULES: 'RULES',
    OPERATIONS: 'OPERATIONS',
};

export const DATA_SOURCE: { [key: string]: string } = {
    LIST: 'LIST',
    CONNECTOR: 'CONNECTOR',
};

export const PREVIEW_MODE: { [key: string]: PreviewMode } = {
    MOBILE: 'MOBILE',
    TABLET: 'TABLET',
    DESKTOP: 'DESKTOP',
};

export const PAGE_ELEMENT_TYPES: { [key: string]: PageElementType } = {
    container: 'CONTAINER',
    component: 'COMPONENT',
};

export const CONTAINER_TYPE: { [key: string]: ContainerType } = {
    row: 'VERTICAL_FLOW',
    column: 'HORIZONTAL_FLOW',
    inline: 'INLINE_FLOW',
    group: 'GROUP',
    chart: 'CHARTS',
};

export const CONTAINER_LABELS: { [key: string]: string } = {
    VERTICAL_FLOW: 'Row',
    HORIZONTAL_FLOW: 'Column',
    INLINE_FLOW: 'Inline',
    GROUP: 'Tabs',
    CHARTS: 'Chart',
};

export const filterOptions: { [key: string]: FilterType } = {
    NONE: 'DO_NOT_FILTER',
    UNIQUE: 'FILTER_BY_UNIQUE',
    WHERE: 'FILTER_BY_WHERE',
};

export const COMPONENT_CONFIGURATION_OPTIONS: { [key: string]: string } = {
    NAME: 'NAME',
    LABEL: 'LABEL',
    CONTENT: 'CONTENT',
    IMAGE_URI: 'IMAGE_URI',
    SOURCE: 'SOURCE',
    FILENAME: 'FILENAME',
    TARGET: 'TARGET',
    WIDTH: 'WIDTH',
    HEIGHT: 'HEIGHT',
    EDITABLE: 'EDITABLE',
    MULTISELECT: 'MULTISELECT',
    AUTO_UPLOAD: 'AUTO_UPLOAD',
    SORTABLE_COLUMNS: 'SORTABLE_COLUMNS',
    EXPORT_CSV_BUTTON: 'EXPORT_CSV_BUTTON',
    CSV_FILE_NAME: 'CSV_FILE_NAME',
    AUTOCOMPLETE: 'AUTOCOMPLETE',
    PAYMENT_CARD_CARD_NUMBER_LABEL: 'PAYMENT_CARD_CARD_NUMBER_LABEL',
    PAYMENT_CARD_EXPIRY_DATE_LABEL: 'PAYMENT_CARD_EXPIRY_DATE_LABEL',
    PAYMENT_CARD_SECURITY_CODE_LABEL: 'PAYMENT_CARD_SECURITY_CODE_LABEL',
    PAYMENT_CARD_POSTAL_CODE_LABEL: 'PAYMENT_CARD_POSTAL_CODE_LABEL',
    PAYMENT_CARD_NAME_ON_CARD_LABEL: 'PAYMENT_CARD_NAME_ON_CARD_LABEL',
    SEARCHABLE: 'SEARCHABLE',
    SEARCHABLEDISPLAYCOLUMNS: 'SEARCHABLEDISPLAYCOLUMNS',
    SEARCHTERMPERSISTED: 'SEARCHTERMPERSISTED',
    STATE_VALUE: 'STATE_VALUE',
    DATA_SOURCE_LIST: 'DATA_SOURCE_LIST',
    DATA_SOURCE_LIST_VALUE: 'DATA_SOURCE_LIST_VALUE',
    DATA_SOURCE_CONNECTOR: 'DATA_SOURCE_CONNECTOR',
    DATA_SOURCE_CONNECTOR_TYPE: 'DATA_SOURCE_CONNECTOR_TYPE',
    DATA_SOURCE_CONNECTOR_BINDING: 'DATA_SOURCE_CONNECTOR_BINDING',
    DATA_SOURCE_FILTER: 'DATA_SOURCE_FILTER',
    DATA_SOURCE_2: 'DATA_SOURCE_2',
    DATA_PRESENTATION: 'DATA_PRESENTATION',
    FILE_DATA_SOURCE: 'FILE_DATA_SOURCE',
    HELP_INFO: 'HELP_INFO',
    HINT_VALUE: 'HINT_VALUE',
    EDITABLE_COLUMNS: 'EDITABLE_COLUMNS',
    COLUMN_COMPONENT_TYPE: 'COLUMN_COMPONENT_TYPE',
    PIN_COLUMNS: 'PIN_COLUMNS',
    LIST_TYPE: 'LIST_TYPE',
    BULLETED_LIST_TYPE: 'BULLETED_LIST_TYPE',
    NUMBERED_LIST_TYPE: 'NUMBERED_LIST_TYPE',
    PAGE_SIZE: 'PAGE_SIZE',
    NO_RESULTS_MESSAGE: 'NO_RESULTS_MESSAGE',
    SEARCH_TERM: 'SEARCH_TERM',
    ASSISTANT_LABEL: 'ASSISTANT_LABEL',
    USER_LABEL: 'USER_LABEL',
    STEP: 'STEP',
    IS_SLIDER: 'IS_SLIDER',
    PAGINATION_BUTTONS: 'PAGINATION_BUTTONS',
    ACCOUNT_ID: 'ACCOUNT_ID',
    AGENT_ID: 'AGENT_ID',
    SESSION_ID: 'SESSION_ID',
    MASK: 'MASK',
    VOICE_INPUT: 'VOICE_INPUT',
    ALERT_ROLE: 'ALERT_ROLE',
};
